import invariant from 'invariant';
import * as React from 'react';
import BoxBody from 'components/BoxBody';
import Divider from 'components/Divider';
import Input from 'components/Input';
import Image from 'components/Image';
import Link from 'components/Link';
import Row from 'components/Row';
import Text from 'components/Text';
import MagicEmailImage from 'assets/images/magic-email.svg';
import FormElement from 'components/FormElement';
import BoxHeader from 'components/BoxHeader';
import ToastActions from 'actions/ToastActions';
import DeviceActions from 'actions/DeviceActions';
import Button from 'components/ButtonDeprecated';
import BoxFooter from 'components/BoxFooter';
import assertions from 'lib/assertions';
import { stripSpaces } from 'lib/phoneNumbers';
import { pickBy, identity } from 'lodash';
import { InputChangeHandler } from 'types/typescript/eventHandlers';

import { ConfirmableDeviceType, deviceString } from 'entities/Device';

import './styles/LinkDeviceVerificationCodeBox.scss';

export type SendType = 'text' | 'voice';

type DefaultProps = {
  sendType: SendType;
  showFooter: boolean;
};

type Props = {
  onResend?: (newSendType?: SendType) => void;
  // This will replace calling the standard /v2/devices/outbound_verification call
  // Only used in the ForgotPasswordPage
  onSubmit?: (code: string) => void;
  // This gets called after using the normal device verification call if onSubmit isn't provided
  onDone?: (authToken?: string, deviceUid?: string) => void;
  // this is stored and formatted like the user entered it
  submittedAddress: string;
  footer?: React.ReactNode;
  showFooter?: boolean;
  successMessage?: string;
  /* Skip updating device preferences:
   *
   * When adding a new device the backend
   * try to make smart choice about enabling the added device
   * and disabeling preixisting one. This skips it, the added
   * device will be disabled, and current devices will not change.
   */
  skipUpdateDevicePreferences?: boolean;
  deviceType: ConfirmableDeviceType;
  clickEventKey?: string;
  usingMagicLink?: boolean;
} & DefaultProps;

type State = {
  submitDisabled: boolean;
  submitCode: string;
};

class LinkDeviceVerificationCodeBox extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    sendType: 'text',
    showFooter: true,
  };

  state: State = {
    submitDisabled: true,
    submitCode: '',
  };

  handleValidation: InputChangeHandler = (e) => {
    assertions.lengthEqualTo(stripSpaces(e.target.value), 4);
  };

  handleValidChange: InputChangeHandler = (e) => {
    this.setState({ submitDisabled: false, submitCode: stripSpaces(e.target.value) });
  };

  handleInvalidChange: InputChangeHandler = () => {
    this.setState({ submitDisabled: true, submitCode: '' });
  };

  onResend = (newSendType?: SendType) => {
    this.setState({ submitDisabled: true, submitCode: '' });
    if (this.props.onResend) {
      this.props.onResend(newSendType);
    }
  };

  submit = () => {
    if (this.props.onSubmit) {
      this.props.onSubmit(this.state.submitCode);
    } else {
      const onSuccess = (device: any) => {
        if (this.props.onDone) {
          this.props.onDone(device.token, device.uid);
        }

        const readableDevice = this.props.deviceType === 'email' ? 'Email address' : 'Phone number';
        ToastActions.showSuccessToast(
          this.props.successMessage || `${readableDevice} linked to Remind`
        );
      };

      const onError = (response: any) => {
        const errorText =
          response && response.code === 'already_taken'
            ? 'This device is already taken, please contact support to merge the device.'
            : 'Please enter a valid code.';

        ToastActions.showErrorToast(errorText);
      };

      const options = pickBy(
        {
          skipUpdateDevicePreferences: this.props.skipUpdateDevicePreferences,
        },
        identity
      );

      invariant(
        this.props.submittedAddress,
        'LinkDeviceVerificationCodeBox submit without address'
      );

      DeviceActions.createOutboundDevice(
        this.props.submittedAddress,
        this.state.submitCode,
        options
      ).then(onSuccess, onError);
    }
  };

  renderHeader = () => {
    const { usingMagicLink, submittedAddress, deviceType } = this.props;

    let title = 'Enter your code';
    let subtext = (
      <React.Fragment>
        Please enter the 4-digit code sent to
        <br />
        {deviceString(submittedAddress, deviceType)}.
      </React.Fragment>
    );

    if (usingMagicLink) {
      const medium = deviceType === 'email' ? 'email' : 'phone';
      title = `Check your ${medium} to continue`;

      subtext = <React.Fragment />;
    }

    return <BoxHeader title={title} subtext={subtext} />;
  };

  renderBoxBody = () => {
    const { clickEventKey, usingMagicLink, submittedAddress } = this.props;
    const { submitDisabled } = this.state;

    return (
      <BoxBody divided={!usingMagicLink}>
        <div className="LinkDeviceVerificationCodeBox-body">
          {usingMagicLink && (
            <Row size="small">
              <Row>
                <Image src={MagicEmailImage} />
              </Row>
              <Text color="graphite">
                {`Enter the 4-digit code we sent to `}
                <Text weight="semibold">{submittedAddress}</Text>
              </Text>
              <Divider size="medium" />
            </Row>
          )}
          <FormElement
            label="Verification code"
            labelHidden
            validate={this.handleValidation}
            onValidChange={this.handleValidChange}
            onInvalidChange={this.handleInvalidChange}
            element={Input}
            elementProps={{
              className: 'LinkDeviceVerificationCodeBox-body-Input',
              name: 'code',
              size: 'large',
              type: 'text',
              placeholder: '0000',
            }}
          />
          <Button
            className="LinkDeviceVerificationCodeBox-body-Submit"
            eventElementName="LinkDeviceVerificationCodeBox-body-Submit"
            eventKey={clickEventKey}
            onClick={this.submit}
            type="submit"
            disabled={submitDisabled}
          >
            Submit
          </Button>
        </div>
      </BoxBody>
    );
  };

  renderFooter = () => {
    const { deviceType, sendType, footer } = this.props;

    const footerConfirmationMedium = deviceType === 'email' ? 'email' : 'text';
    const footerText = `Didn't receive a confirmation ${footerConfirmationMedium}?`;
    const oppositeSendType = sendType === 'text' ? 'voice' : 'text';

    return (
      <BoxFooter className="LinkDeviceVerificationCodeBox-footer">
        <div className="LinkDeviceVerificationCodeBox-footer-section">
          {footerText}{' '}
          <Link data-test="resend-link" onClick={() => this.onResend()}>
            Resend
          </Link>
          {footer ? ' ' : null}
          {footer}
        </div>
        {deviceType === 'phoneNumber' ? (
          <div className="LinkDeviceVerificationCodeBox-footer-section">
            <Link data-test="sendtype-change-link" onClick={() => this.onResend(oppositeSendType)}>
              {sendType === 'text' ? 'Call me instead' : 'Send me a text instead'}
            </Link>
          </div>
        ) : null}
      </BoxFooter>
    );
  };

  render() {
    const { submittedAddress, deviceType, sendType } = this.props;
    const subtext = (
      <React.Fragment>
        Please enter the 4-digit code sent to
        <br />
        {deviceString(submittedAddress, deviceType)}.
      </React.Fragment>
    );

    return (
      <div className="LinkDeviceVerificationCodeBox">
        {this.renderHeader()}
        {this.renderBoxBody()}
        {this.props.showFooter && this.renderFooter()}
      </div>
    );
  }
}

export default LinkDeviceVerificationCodeBox;
